import { useEffect, useState } from 'react'
import { useSelector } from 'react-redux'

import { useQuery } from '@tanstack/react-query'
import { AxiosError } from 'axios'
import moment from 'moment'
import { RootState } from 'typesafe-actions'

import { Account } from '@driftt/api-types'

import { fetchAccountEngagementSummary, getAccountExplain } from 'api/AccountActivityAPI'
import { getAccountById } from 'api/AccountAPI'
import { fetchSalesforceByDomain } from 'api/ConversationAPI'
import { fetchSalesforceOpportunities } from 'api/OpportunitiesAPI'
import { AccountActivity, CompanyData, ScoreBreakdown } from 'modules/accountActivity/domain'

export interface AccountSiteVisitInfo {
  pageUrl: string
  numberOfVisits: number
}

interface AccountDetailsData {
  hasSfdc: boolean
  isLoading: boolean
  accountData: Account | undefined
  accountError: AxiosError | null
  companyData: CompanyData | undefined
  explainData: AccountActivity | undefined
  explainError: AxiosError | null
  salesforceData: any
  opportunities: any
  opportunitiesError: AxiosError | null
  accountFitScore: number | undefined
  accountScoreBreakdown: ScoreBreakdown | undefined
  topPagesAndTopics: any
  topPagesAndTopicsError: AxiosError | null
}

export const useAccountDetails = (accountId: string): AccountDetailsData => {
  const hasSfdc: boolean = useSelector((state: RootState) => state.integrations.status.salesforce)

  const [isLoading, setIsLoading] = useState<boolean>(true)

  const {
    data: accountAndSalesforceData,
    error: accountError,
    isLoading: accountLoading
  } = useQuery<any, AxiosError>({
    queryKey: ['getAccountById-fetchSalesforceByDomain', accountId],
    queryFn: async () => {
      const accountData = await getAccountById(accountId)
      const salesforceData = await fetchSalesforceByDomain(accountData.domain)
      return { accountData, salesforceData }
    }
  })

  const { data: explainData, error: explainError, isLoading: explainLoading } = useQuery<
    AccountActivity,
    AxiosError
  >({
    queryKey: ['getAccountExplain', accountId],
    queryFn: () => getAccountExplain({ accountId, limit: 10 })
  })

  const {
    data: opportunities,
    error: opportunitiesError,
    isLoading: opportunitiesLoading
  } = useQuery<any, AxiosError>({
    queryKey: ['fetchSalesforceOpportunities', accountId],
    queryFn: () => fetchSalesforceOpportunities(accountId)
  })

  const { data: topPagesAndTopics, error: topPagesAndTopicsError } = useQuery<any, AxiosError>({
    queryKey: ['fetchAccountEngagementSummary', accountId],
    queryFn: async () => {
      const { pageViewCounts, topics } = await fetchAccountEngagementSummary(
        accountId,
        moment()
          .subtract(2, 'week')
          .startOf('day')
          .valueOf(),
        moment()
          .endOf('day')
          .valueOf(),
        false
      )
      return { topPages: pageViewCounts as AccountSiteVisitInfo[], topics: topics as string[] }
    }
  })

  useEffect(() => {
    if (!accountLoading && !explainLoading && !opportunitiesLoading) {
      setIsLoading(false)
    }
  }, [accountLoading, explainLoading, opportunitiesLoading])

  return {
    hasSfdc,
    isLoading,
    accountData: accountAndSalesforceData?.accountData,
    accountError,
    companyData: explainData?.companyData,
    explainData,
    explainError,
    salesforceData: accountAndSalesforceData?.salesforceData,
    opportunities,
    opportunitiesError,
    accountFitScore: explainData?.accountFitScore,
    accountScoreBreakdown: explainData?.featureWeights,
    topPagesAndTopics,
    topPagesAndTopicsError
  }
}
